import React, { useState } from 'react';
import { Car, MapPin, Activity, Lock, Unlock, Plus, X } from 'lucide-react';
import { useVehicleStore } from '../store';
import { vehicleService } from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import VehicleTypeIcon, { getVehicleTypeLabel } from './VehicleTypeIcon';

const VehiclePanel = ({ vehicles }) => {
  const { selectVehicle, selectedVehicle, updateVehicle, addVehicle } = useVehicleStore();
  const [loading, setLoading] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    plate: '',
    brand: '',
    model: '',
    year: '',
    color: '',
    type: 'car',
    imei: '',
  });

  const handleSelectVehicle = (vehicle) => {
    selectVehicle(vehicle);
  };

  const handleToggleBlock = async (vehicle, e) => {
    e.stopPropagation();
    setLoading(vehicle.id);

    try {
      const response = await vehicleService.toggleBlock(vehicle.id);
      updateVehicle(vehicle.id, response.data.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Erro ao alterar bloqueio:', error);
      toast.error('Erro ao alterar bloqueio do veículo');
    } finally {
      setLoading(null);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      plate: '',
      brand: '',
      model: '',
      year: '',
      color: '',
      type: 'car',
      imei: '',
    });
  };

  const handleCreateVehicle = async (e) => {
    e.preventDefault();
    setLoading('create');

    try {
      const response = await vehicleService.create(formData);
      addVehicle(response.data.data);
      toast.success('Veículo cadastrado com sucesso');
      setShowCreateForm(false);
      resetForm();
    } catch (error) {
      console.error('Erro ao cadastrar veículo:', error);
      toast.error(error.response?.data?.message || 'Erro ao cadastrar veículo');
    } finally {
      setLoading(null);
    }
  };

  const getVehicleStatus = (vehicle) => {
    if (vehicle.is_blocked) {
      return { text: 'Bloqueado', class: 'badge-danger' };
    }
    if (!vehicle.is_active) {
      return { text: 'Offline', class: 'badge-warning' };
    }
    if (vehicle.positions?.[0]?.motion) {
      return { text: 'Em movimento', class: 'badge-success' };
    }
    return { text: 'Parado', class: 'badge-info' };
  };

  const getLastUpdate = (vehicle) => {
    if (vehicle.positions?.[0]?.device_time) {
      return format(new Date(vehicle.positions[0].device_time), 'dd/MM HH:mm');
    }
    return 'Sem dados';
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">Veículos</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="p-2 rounded-lg bg-primary-500 hover:bg-primary-600 transition-colors"
          title="Cadastrar veículo"
        >
          <Plus className="w-5 h-5 text-white" />
        </button>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {vehicles.length === 0 ? (
          <div className="text-center py-8 text-dark-400">
            <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhum veículo cadastrado</p>
          </div>
        ) : (
          vehicles.map((vehicle) => {
            const status = getVehicleStatus(vehicle);
            const isSelected = selectedVehicle?.id === vehicle.id;

            return (
              <div
                key={vehicle.id}
                onClick={() => handleSelectVehicle(vehicle)}
                className={`
                  p-4 rounded-lg cursor-pointer transition-all duration-200
                  ${isSelected
                    ? 'bg-primary-500/20 border-2 border-primary-500'
                    : 'bg-dark-700 hover:bg-dark-600 border-2 border-transparent'
                  }
                `}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-primary-500/30 bg-primary-500/10 text-primary-300">
                      <VehicleTypeIcon vehicle={vehicle} className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-white mb-1">{vehicle.name}</h3>
                      <p className="text-sm text-dark-400">{vehicle.plate} - {getVehicleTypeLabel(vehicle)}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={(e) => handleToggleBlock(vehicle, e)}
                    disabled={loading === vehicle.id}
                    className={`
                      p-2 rounded-lg transition-colors
                      ${vehicle.is_blocked
                        ? 'bg-danger-500/20 text-danger-400 hover:bg-danger-500/30'
                        : 'bg-success-500/20 text-success-400 hover:bg-success-500/30'
                      }
                    `}
                  >
                    {loading === vehicle.id ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                    ) : vehicle.is_blocked ? (
                      <Lock className="w-5 h-5" />
                    ) : (
                      <Unlock className="w-5 h-5" />
                    )}
                  </button>
                </div>

                <div className="flex items-center gap-4 text-sm text-dark-400 mb-2">
                  {vehicle.positions?.[0]?.speed !== undefined && (
                    <div className="flex items-center gap-1">
                      <Activity className="w-4 h-4" />
                      <span>{Number(vehicle.positions[0].speed).toFixed(0)} km/h</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{getLastUpdate(vehicle)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`badge ${status.class}`}>
                    {status.text}
                  </span>
                  
                  {vehicle.positions?.[0]?.ignition && (
                    <span className="badge badge-info">
                      Ligado
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showCreateForm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-2xl rounded-lg border border-dark-700 bg-dark-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-dark-800 p-5">
              <div>
                <h3 className="text-lg font-bold text-white">Cadastrar veículo</h3>
                <p className="text-sm text-dark-400">Adicione um veículo à sua frota.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="rounded-lg p-2 text-dark-400 transition-colors hover:bg-dark-800 hover:text-white"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreateVehicle} className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">
                    Nome
                  </label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className="input-field"
                    placeholder="Ex: Frota Centro"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">
                    Placa
                  </label>
                  <input
                    name="plate"
                    value={formData.plate}
                    onChange={handleFormChange}
                    className="input-field uppercase"
                    placeholder="ABC-1234"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">
                    Marca
                  </label>
                  <input
                    name="brand"
                    value={formData.brand}
                    onChange={handleFormChange}
                    className="input-field"
                    placeholder="Fiat"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">
                    Modelo
                  </label>
                  <input
                    name="model"
                    value={formData.model}
                    onChange={handleFormChange}
                    className="input-field"
                    placeholder="Strada"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">
                    Ano
                  </label>
                  <input
                    name="year"
                    type="number"
                    value={formData.year}
                    onChange={handleFormChange}
                    className="input-field"
                    placeholder="2024"
                    min="1950"
                    max="2100"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">
                    Cor
                  </label>
                  <input
                    name="color"
                    value={formData.color}
                    onChange={handleFormChange}
                    className="input-field"
                    placeholder="Branco"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">
                    Tipo
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                    className="input-field"
                  >
                    <option value="car">Carro</option>
                    <option value="motorcycle">Moto</option>
                    <option value="truck">Caminhão</option>
                    <option value="van">Van</option>
                    <option value="bus">Ônibus</option>
                    <option value="other">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">
                    IMEI do rastreador
                  </label>
                  <input
                    name="imei"
                    value={formData.imei}
                    onChange={handleFormChange}
                    className="input-field"
                    placeholder="860000000000000"
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-dark-800 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading === 'create'}
                  className="btn-primary"
                >
                  {loading === 'create' ? 'Salvando...' : 'Salvar veículo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclePanel;
